// Coquille de l'application (charte D-038) : en-tête clair avec logo, nom, navigation par rôle soulignée,
// recherche, mention de présentation, bascule Jour / Nuit persistante, avatar à initiales ; sous-barre
// (retour, fil d'Ariane, filtre global de territoire) ; aide contextuelle ; pied de page ; navigation
// inférieure à icônes sur téléphone.

import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CODES_PROVINCES, type EntreeNavigation, type Preferences } from '@carburanttrack/shared';
import { api } from '../api/client';
import { useAPropos } from '../api/requetes';
import { Icone, type NomIcone } from '../composants/Icones';
import { libelleProvince, libelleRole } from '../composants/format';
import { useFiltres } from './filtres';
import { filAriane, pageDe } from './routes';
import { useSession } from './session';
import { appliquerTheme } from './theme';
import { AccesMessagerie } from '../modules/messagerie';
import { ClocheNotifications, FournisseurTempsReel } from '../modules/notifications';
import { BandeauHorsLigne, FournisseurHorsLigne } from '../horsligne/contexte';

/** Bascule Jour / Nuit : effet immédiat, puis enregistrement dans les préférences du serveur (R-11). */
export function useBasculeTheme(persister = true) {
  const client = useQueryClient();
  // Hors session (page de connexion), aucune lecture des préférences : la bascule reste locale.
  const prefs = useQuery({ queryKey: ['preferences'], queryFn: async () => (await api<Preferences>('/api/v1/preferences')).donnees, enabled: persister });
  const enregistrement = useMutation({
    mutationFn: async (p: Preferences) => (await api<Preferences>('/api/v1/preferences', { methode: 'PUT', corps: p })).donnees,
    onSuccess: (p) => client.setQueryData(['preferences'], p),
  });
  const effectif = (): 'jour' | 'nuit' => (document.documentElement.dataset.theme === 'nuit' ? 'nuit' : 'jour');
  return {
    effectif,
    basculer: () => {
      const nouveau = effectif() === 'nuit' ? 'jour' : 'nuit';
      appliquerTheme(nouveau);
      if (prefs.data) enregistrement.mutate({ ...prefs.data, theme: nouveau });
    },
  };
}

export function BoutonTheme({ avecLibelle = true, persister = true }: { avecLibelle?: boolean; persister?: boolean }) {
  const { effectif, basculer } = useBasculeTheme(persister);
  const [, rafraichir] = useState(0);
  const nuit = effectif() === 'nuit';
  return (
    <button
      type="button"
      className="bouton bascule-theme"
      aria-label={nuit ? 'Passer en mode jour' : 'Passer en mode nuit'}
      title={nuit ? 'Passer en mode jour' : 'Passer en mode nuit'}
      onClick={() => {
        basculer();
        rafraichir((n) => n + 1);
      }}
    >
      <Icone nom={nuit ? 'soleil' : 'lune'} />
      {avecLibelle && <span className="libelle-bouton">{nuit ? 'Jour' : 'Nuit'}</span>}
    </button>
  );
}

const ICONES_NAVIGATION: Record<string, NomIcone> = {
  accueil: 'accueil',
  declarer: 'formulaire',
  livraisons: 'camion',
  messages: 'message',
  missions: 'camion',
  inspections: 'controle',
  flotte: 'camion',
  stocks: 'cuve',
  alertes: 'incident',
  referentiel: 'liste',
  documents: 'document',
  incidents: 'incident',
  controle: 'controle',
};

/**
 * Entrées secondaires de l'en-tête (référentiels, documents, administration), regroupées dans le menu « Plus » ;
 * au-delà de cinq entrées principales, les suivantes y passent aussi, pour que l'en-tête tienne sur une ligne
 * à 1 440 pixels quel que soit le profil.
 */
const CODES_SECONDAIRES = new Set(['referentiel', 'catalogue', 'documents', 'administration', 'integrations']);
const PRINCIPALES_MAX = 5;

export function repartirNavigation(navigation: readonly EntreeNavigation[]): { principales: EntreeNavigation[]; secondaires: EntreeNavigation[] } {
  const principales = navigation.filter((e) => !CODES_SECONDAIRES.has(e.code)).slice(0, PRINCIPALES_MAX);
  return { principales, secondaires: navigation.filter((e) => !principales.includes(e)) };
}

/** Menu « Plus » (navigation par divulgation) : flèches, Échap qui rend le focus au bouton, fermeture au clic extérieur. */
function MenuPlus({ entrees, estActif }: { entrees: EntreeNavigation[]; estActif: (chemin: string) => boolean }) {
  const [ouvert, setOuvert] = useState(false);
  const conteneur = useRef<HTMLLIElement>(null);
  const bouton = useRef<HTMLButtonElement>(null);
  const lieu = useLocation();
  useEffect(() => setOuvert(false), [lieu.pathname, lieu.search]);
  useEffect(() => {
    if (!ouvert) return;
    const surClic = (e: MouseEvent) => {
      if (!conteneur.current?.contains(e.target as Node)) setOuvert(false);
    };
    document.addEventListener('mousedown', surClic);
    return () => document.removeEventListener('mousedown', surClic);
  }, [ouvert]);
  const liens = () => [...(conteneur.current?.querySelectorAll<HTMLAnchorElement>('.menu-plus-liste a') ?? [])];
  const deplacer = (pas: number) => {
    const l = liens();
    if (l.length === 0) return;
    const i = l.indexOf(document.activeElement as HTMLAnchorElement);
    l[(i + pas + l.length) % l.length]!.focus();
  };
  const surTouche = (e: KeyboardEvent<HTMLLIElement>) => {
    if (e.key === 'Escape' && ouvert) {
      e.preventDefault();
      setOuvert(false);
      bouton.current?.focus();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!ouvert) {
        setOuvert(true);
        requestAnimationFrame(() => (e.key === 'ArrowDown' ? liens()[0] : liens().at(-1))?.focus());
      } else deplacer(e.key === 'ArrowDown' ? 1 : -1);
    }
  };
  const actif = entrees.find((e) => estActif(e.chemin));
  return (
    <li
      ref={conteneur}
      className="menu-plus"
      onKeyDown={surTouche}
      onBlur={(e) => {
        if (!conteneur.current?.contains(e.relatedTarget as Node | null)) setOuvert(false);
      }}
    >
      <button ref={bouton} type="button" className={actif ? 'actif' : undefined} aria-expanded={ouvert} aria-controls="menu-plus-liste" onClick={() => setOuvert((v) => !v)}>
        Plus
        {actif && <span className="visuellement-cache">, page en cours : {actif.libelle}</span>}
        <Icone nom="chevron" taille={14} />
      </button>
      <ul id="menu-plus-liste" className="menu-plus-liste" hidden={!ouvert}>
        {entrees.map((e) => (
          <li key={e.code}>
            <NavLink to={e.chemin} aria-current={estActif(e.chemin) ? 'page' : undefined}>
              {e.libelle}
            </NavLink>
          </li>
        ))}
      </ul>
    </li>
  );
}

function initiales(prenom: string, nom: string): string {
  return `${prenom.trim().charAt(0)}${nom.trim().charAt(0)}`.toUpperCase();
}

export function Coquille({ children }: { children: ReactNode }) {
  const { etat, deconnecter } = useSession();
  const { province, definirProvince } = useFiltres();
  const lieu = useLocation();
  const naviguer = useNavigate();
  const aPropos = useAPropos();
  const [aideOuverte, setAideOuverte] = useState(false);
  const [requete, setRequete] = useState('');
  const page = pageDe(lieu.pathname);
  const fil = filAriane(lieu.pathname);
  const navigation = etat?.navigation ?? [];
  const { principales, secondaires } = repartirNavigation(navigation);
  const peutRechercher = Boolean(etat?.roles?.some((r) => !['support_technique', 'administrateur_idg', 'citoyen'].includes(r)));

  function rechercher(e: FormEvent) {
    e.preventDefault();
    if (requete.trim().length >= 2) naviguer(`/recherche?q=${encodeURIComponent(requete.trim())}`);
  }

  const estActif = (chemin: string) => {
    const base = chemin.split('?')[0]!;
    return base === '/' ? lieu.pathname === '/' : lieu.pathname.startsWith(base);
  };
  const utilisateur = etat?.utilisateur;

  return (
    <FournisseurHorsLigne>
    <FournisseurTempsReel actif={!etat?.horsLigne}>
    <div className="coquille">
      <a href="#contenu" className="visuellement-cache">
        Aller au contenu
      </a>
      <header className="barre">
        <Link to="/" className="marque" aria-label="CarburantTrack, accueil">
          <img src="/marque/pictogramme.png" alt="" className="seulement-jour" />
          <img src="/marque/pictogramme-clair.png" alt="" className="seulement-nuit" />
          <span>CarburantTrack</span>
        </Link>
        <nav aria-label="Navigation principale">
          <ul>
            {principales.map((e) => (
              <li key={e.code}>
                <NavLink to={e.chemin} aria-current={estActif(e.chemin) ? 'page' : undefined}>
                  {e.libelle}
                </NavLink>
              </li>
            ))}
            {secondaires.length > 0 && <MenuPlus entrees={secondaires} estActif={estActif} />}
          </ul>
        </nav>
        <div className="droite">
          {peutRechercher && (
            <form className="recherche" role="search" onSubmit={rechercher}>
              <label htmlFor="recherche-globale" className="visuellement-cache">
                Recherche globale
              </label>
              <input id="recherche-globale" type="search" placeholder="Rechercher une installation, une organisation, un document" value={requete} onChange={(e) => setRequete(e.target.value)} />
            </form>
          )}
          {aPropos.data?.environnement.mention && (
            <span className="environnement" title={aPropos.data.environnement.mention}>
              <span className="env-long">{aPropos.data.environnement.nom}, données fictives</span>
              <span className="env-court">Données fictives</span>
            </span>
          )}
          <AccesMessagerie />
          <ClocheNotifications />
          <BoutonTheme />
          <button type="button" className="bouton bouton-aide" title="Aide" aria-expanded={aideOuverte} aria-controls="aide-contextuelle" onClick={() => setAideOuverte((v) => !v)}>
            <Icone nom="aide" />
            <span className="libelle-bouton">Aide</span>
          </button>
          <button type="button" className="bouton" title="Se déconnecter" onClick={() => void deconnecter().then(() => naviguer('/connexion'))}>
            <Icone nom="sortie" />
            <span className="libelle-bouton">Se déconnecter</span>
          </button>
          <Link
            className="bouton avatar"
            to="/preferences"
            aria-label={utilisateur ? `Compte de ${utilisateur.prenom} ${utilisateur.nom}, préférences` : 'Compte et préférences'}
            title={utilisateur ? `${utilisateur.prenom} ${utilisateur.nom}` : 'Compte'}
          >
            {utilisateur ? initiales(utilisateur.prenom, utilisateur.nom) : 'CT'}
          </Link>
        </div>
      </header>
      <div className="sous-barre">
        {page?.parent !== null && page?.parent !== undefined && (
          <button type="button" className="bouton discret" onClick={() => (window.history.length > 1 ? naviguer(-1) : naviguer(page.parent!))}>
            <Icone nom="retour" />
            Retour
          </button>
        )}
        <nav className="fil-ariane" aria-label="Fil d’Ariane">
          <ol>
            {fil.map((f, i) => (
              <li key={f.chemin}>{i === fil.length - 1 ? <span aria-current="page">{f.libelle}</span> : <Link to={f.chemin}>{f.libelle}</Link>}</li>
            ))}
          </ol>
        </nav>
        <div className="filtres">
          <label htmlFor="filtre-territoire">Territoire</label>
          <select id="filtre-territoire" value={province ?? ''} onChange={(e) => definirProvince(e.target.value || null)}>
            <option value="">National</option>
            {CODES_PROVINCES.map((c) => (
              <option key={c} value={c}>
                {libelleProvince(c)}
              </option>
            ))}
          </select>
          <span className="attenue">{etat?.roles?.map(libelleRole).join(', ')}</span>
        </div>
      </div>
      <main id="contenu" className="contenu" tabIndex={-1}>
        <BandeauHorsLigne horsLigneSession={etat?.horsLigne} />
        {children}
      </main>
      {aideOuverte && (
        <aside id="aide-contextuelle" className="panneau-aide" aria-label="Aide contextuelle">
          <h2>{page?.libelle ?? 'Aide'}</h2>
          <p>{page?.aide ?? 'Aucune aide spécifique pour cette page.'}</p>
          <button type="button" className="bouton" onClick={() => setAideOuverte(false)}>
            Fermer l’aide
          </button>
        </aside>
      )}
      <footer className="pied">
        <span>CarburantTrack {aPropos.data ? `version ${aPropos.data.version}` : ''}</span>
        {aPropos.data?.environnement.mention && <span>{aPropos.data.environnement.mention}</span>}
        <Link to="/a-propos">À propos</Link>
      </footer>
      <nav className="navigation-mobile" aria-label="Navigation sur téléphone">
        {navigation
          .filter((e) => e.mobile)
          .slice(0, 4)
          .map((e) => (
            <NavLink key={e.code} to={e.chemin} aria-current={estActif(e.chemin) ? 'page' : undefined}>
              <Icone nom={ICONES_NAVIGATION[e.code] ?? 'liste'} />
              {e.libelle}
            </NavLink>
          ))}
        <NavLink to="/preferences" aria-current={estActif('/preferences') ? 'page' : undefined}>
          <Icone nom="plus" />
          Plus
        </NavLink>
      </nav>
    </div>
    </FournisseurTempsReel>
    </FournisseurHorsLigne>
  );
}
